<template>
    <article class="card-site" :class="type" :id="item.id" v-if="item.fields">
        <NuxtLink class="image-wrapper" :to="`${url}${item.id}`" @click.native="saveSite">
            <mess-image :src="photo" loading="lazy" :sizes="imageSizes" />
        </NuxtLink>
        <div class="content">
            <div class="copy">
                <p v-if="type !== 'large' && neighborhoodName" class="hood">{{ neighborhoodName }}</p>
                <NuxtLink :to="`${url}${item.id}`" @click.native="saveSite">
                    <h3 class="headline-2">{{ item.fields['Name'] }}</h3>
                </NuxtLink>
                <p v-if="type === 'large' && neighborhoodName" class="gray-tag">{{ neighborhoodName }}</p>
                <p v-if="item && item.fields && item.fields['Site Address']" class="address" :class="{ 'body-small': type !== 'map', 'body-book': type === 'map' }">
                    {{ item.fields['Site Address'] }}
                </p>
            </div>
        </div>
    </article>
</template>

<script setup>
    // Stores
    import { useArchivesNeighborhoodsStore } from '@/stores/archives-neighborhoods';
    const neighborhoodsStore = useArchivesNeighborhoodsStore();
    const neighborhoodsMap = neighborhoodsStore.neighborhoodsMap;

    // Composables
    const { getSiteNeighborhoodName } = useUtils();
    const { getImageFromArray } = useImageUrl();

    // Emits
    const emit = defineEmits(['updateSingleItem']);

    // Props
    const props = defineProps({
        itinerary: {
            type: Object,
            required: false,
            default: {},
        },
        item: {
            type: Object,
            required: true,
        },
        type: {
            type: String,
            required: false,
            default: 'large',
        },
    });
    const { item, type } = toRefs(props);

    // Same card geometry as Cards/site.vue, so the same slot map applies.
    const imageSizes = computed(() => SIZES_SITE_CARD[type.value] || SIZES_SITE_CARD.large);

    // Variables
    const neighborhoodName = getSiteNeighborhoodName(item, neighborhoodsMap);
    const route = useRoute();

    // Computed
    const url = computed(() => {
        return '/archive/sites/';
    });

    const photo = computed(() => {
        if (item.value?.fields?.['Photos']) {
            return getImageFromArray(item.value.fields['Photos']);
        }
        return '';
    });

    // Methods
    const saveSite = () => {
        if (!window.siteQuery) {
            window.siteQuery = {
                savedSite: item.value.id,
            };
        } else {
            window.siteQuery.savedSite = item.value.id;
        }
    };
    function updateSingleItem(item) {
        emit('updateSingleItem', item);
    }
</script>

<style lang="scss" scoped>
    .card-site {
        background-color: $white;

        .image-wrapper {
            display: block;
            position: relative;
            margin-bottom: 0.5em;
        }

        .hood {
            margin: 6px 0;
            font-size: 9px;
            font-weight: 800;
            line-height: 10px;
            letter-spacing: 0.72px;
            text-transform: uppercase;
        }

        img {
            position: absolute;
            inset: 0 0 0 0;
            height: 100%;
            width: 100%;
            object-fit: cover;
            object-position: center;
        }

        &.large {
            .image-wrapper {
                padding-top: 60%;
                height: 0;
                width: 100%;

                @media (min-width: $tablet) {
                    padding-top: 30%;
                    width: 50%;
                }
            }

            .gray-tag {
                margin: 10px 0;
            }

            .content {
                padding: 10px 0;
                width: 100%;
            }
        }

        &.list,
        &.map {
            display: flex;

            .image-wrapper {
                width: 70px;
                height: 70px;
                flex-shrink: 0;
                margin-right: 17px;
            }

            h3 {
                margin-bottom: 10px;
            }
        }

        &.map {
            box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.25);
            padding: 15px;

            .gray-tag {
                margin: 0 0 12px;

                @media (min-width: $tablet) {
                    margin: 12px 0;
                }
            }

            .footer {
                margin-top: 12px;
            }
        }

        &.list {
            padding: 20px 0;
            border-bottom: 1px solid #9d9fa2;

            .gray-tag {
                margin: 0 0 6px;
            }

            .content {
                display: flex;
                flex-grow: 1;
            }

            .footer {
                flex-grow: 0;
                flex-shrink: 0;
                margin: 0;
                flex-direction: column;

                button {
                    margin: 0;

                    + button {
                        margin-top: 18px;
                    }
                }
            }

            .copy {
                padding-right: 18px;
                flex-grow: 1;
            }
        }

        .footer {
            display: flex;
            margin-top: 10px;

            button {
                margin-right: 15px;
            }
        }

        .headline-3 {
            padding-right: 9%;
        }

        @media (min-width: $tablet) {
            flex-direction: column;
            height: 100%;

            &.list {
                border-bottom: none;
                padding: 5px 0;

                .footer {
                    flex-direction: row;
                    margin-top: 20px;

                    button + button {
                        margin-top: 0;
                        margin-left: 12px;
                    }
                }
            }

            &.list .image-wrapper {
                margin-bottom: 15px;
                margin-right: 0px;
                width: 100%;
                height: 165px;
            }

            .content {
                flex-direction: column;
            }
        }
    }
</style>
